import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { PALS, ELEMENTS, ELEMENT_COLORS, type Element, type Pal } from "@/lib/pals-data";
import { PalPortrait } from "@/components/PalPortrait";
import { useIncrementalList } from "@/lib/use-incremental";
import { FaqSection, RelatedGuides, faqSchema, type FaqItem } from "@/components/PageFaq";

const ROLES = [
  { key: "pve", label: "PvE combat", field: "pveRating" as const },
  { key: "pvp", label: "PvP ladder", field: "pvpRating" as const },
  { key: "base", label: "Base work", field: "baseRating" as const },
  { key: "mount", label: "Mount speed", field: "mountRating" as const },
];

const TIERS = ["SS", "S", "A", "B", "C"] as const;
type Tier = (typeof TIERS)[number];

const TIER_COLORS: Record<Tier, string> = {
  SS: "#F97316",
  S: "#EF4444",
  A: "#EAB308",
  B: "#38BDF8",
  C: "#94A3B8",
};

/** Ratings are 1-10; buckets are fixed so a Pal's tier is stable across views. */
function tierFor(rating: number): Tier {
  if (rating >= 9.5) return "SS";
  if (rating >= 8.5) return "S";
  if (rating >= 7) return "A";
  if (rating >= 5.5) return "B";
  return "C";
}

const FAQ: FaqItem[] = [
  {
    q: "How is the Palworld tier list calculated?",
    a: "Each Pal is scored 1-10 for PvE combat, PvP ladder, base work and mount speed. Scores of 9.5+ land in SS, 8.5-9.4 in S, 7-8.4 in A, 5.5-6.9 in B and anything below that in C. Switching the role filter re-ranks every Pal against that role only, so a base-work specialist can be S tier for work and C tier in PvP.",
  },
  {
    q: "What is the best Pal in Palworld right now?",
    a: "For raw PvE damage the top of the S tier is dominated by Legendary Dragon and Dark Pals with high attack plus a fast ride speed, because they clear towers and travel between bases without a swap. Filter this tier list by PvE combat and Legendary rarity to see the current top six ranked by score.",
  },
  {
    q: "Does the best PvP Pal differ from the best PvE Pal?",
    a: "Yes. PvP scales cooldowns and reduces one-shot burst, so sustained-damage and utility Pals climb while glass-cannon nukers fall. Toggle between the PvE combat and PvP ladder filters to see the two orders side by side.",
  },
  {
    q: "Which Pals are best for base work in Palworld?",
    a: "Base tiers weigh work speed and the number of work suitabilities rather than attack. Choose the Base work role filter to rank every Pal by throughput, then pair a Kindling and a Handiwork Pal of the same tier so your production line never idles.",
  },
  {
    q: "Which Palworld mount is the fastest?",
    a: "Mount tiers rank ride speed and stamina together, because a slightly slower flier with deep stamina crosses Palpagos faster than a fast one that lands to recover. Use the Mount speed filter here, then check the mounts chart for exact speed numbers.",
  },
  {
    q: "How often is the tier list updated?",
    a: "The tiers are generated directly from the Pal database, so any stat, roster or balance update to Pal Stars re-ranks this page automatically on the next page view.",
  },
];

type TierListSearch = { role?: "pve" | "pvp" | "base" | "mount"; element?: string; rarity?: string; tier?: string };

// Plain validator (no zod) keeps ~57 KB of JavaScript off this route.
// Params stay optional and never defaulted: a default makes the router rewrite
// /tier-list to `?role=pve&element=&rarity=&tier=`, producing a duplicate URL
// that Google reports as "Alternate page with proper canonical tag".
const ROLE_KEYS = ["pve", "pvp", "base", "mount"] as const;
function validateTierSearch(search: Record<string, unknown>): TierListSearch {
  const out: TierListSearch = {};
  const role = search.role;
  if (typeof role === "string" && role !== "pve" && (ROLE_KEYS as readonly string[]).includes(role))
    out.role = role as TierListSearch["role"];
  for (const k of ["element", "rarity", "tier"] as const) {
    const v = search[k];
    if (typeof v === "string" && v) out[k] = v;
  }
  return out;
}

const TITLE = "Palworld Tier List — Best Pals by Role";
const DESC =
  "Filterable Palworld tier list: rank every Pal SS to C for PvE, PvP, base work and mount speed, filtered by element and rarity.";

export const Route = createFileRoute("/tier-list")({
  validateSearch: validateTierSearch,
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://palstars.com/tier-list" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESC },
    ],
    links: [{ rel: "canonical", href: "https://palstars.com/tier-list" }],
    scripts: [
      faqSchema(FAQ, "https://palstars.com/tier-list"),
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ItemList",
          name: "Palworld PvE Tier List — Pal Stars",
          description: "Palworld Pals ranked SS to C for PvE combat.",
          itemListOrder: "https://schema.org/ItemListOrderDescending",
          itemListElement: PALS.slice()
            .sort((a, b) => b.pveRating - a.pveRating)
            .slice(0, 50)
            .map((p, i) => ({
              "@type": "ListItem",
              position: i + 1,
              url: `https://palstars.com/pals/${p.slug}`,
              name: `${p.name} — ${tierFor(p.pveRating)} tier`,
            })),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: { "@id": "https://palstars.com/", name: "Home" } },
            { "@type": "ListItem", position: 2, name: "Tier List", item: { "@id": "https://palstars.com/tier-list", name: "Tier List" } },
          ],
        }),
      },
    ],
  }),
  component: TierListPage,
});

function TierListPage() {
  const search = Route.useSearch();
  const role = search.role ?? "pve";
  const element = search.element ?? "";
  const rarity = search.rarity ?? "";
  const tier = search.tier ?? "";
  const navigate = Route.useNavigate();
  const [q, setQ] = useState("");

  const field = ROLES.find((r) => r.key === role)!.field;
  const roleLabel = ROLES.find((r) => r.key === role)!.label;

  const ranked = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return PALS.filter((p) => {
      if (element && p.element !== element && p.secondary !== element) return false;
      if (rarity && p.rarity !== rarity) return false;
      if (tier && tierFor(p[field]) !== tier) return false;
      if (needle && !p.name.toLowerCase().includes(needle)) return false;
      return true;
    })
      .slice()
      .sort((a, b) => b[field] - a[field] || a.name.localeCompare(b.name));
  }, [element, rarity, tier, field, q]);

  const counts = useMemo(() => {
    const c: Record<Tier, number> = { SS: 0, S: 0, A: 0, B: 0, C: 0 };
    for (const p of PALS) c[tierFor(p[field])]++;
    return c;
  }, [field]);

  const { visible, hasMore, remaining, showMore, sentinelRef } = useIncrementalList(ranked, 48);

  // Default/empty values are removed from the query string so each filter state
  // has exactly one canonical URL.
  type TierSearch = { role?: string; element?: string; rarity?: string; tier?: string };
  const set = (patch: TierSearch) =>
    navigate({
      search: ((s: TierSearch) => {
        const next: TierSearch = { ...s, ...patch };
        if (!next.role || next.role === "pve") delete next.role;
        for (const k of ["element", "rarity", "tier"] as const) if (!next[k]) delete next[k];
        return next;
      }) as never,
    });

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 py-10">
      <h1 className="text-3xl sm:text-4xl font-bold">Palworld Tier List</h1>
      <p className="aeo-answer text-muted-foreground mt-2 max-w-3xl">
        Every Pal in the Pal Stars database ranked SS to C. Tiers are generated from four separate role
        scores — PvE combat, PvP ladder, base work and mount speed — so you can filter for the best Pal
        for the job instead of one blended ranking. You are currently viewing <strong>{roleLabel}</strong>.
      </p>

      <div className="glass rounded-xl p-4 mt-6 grid gap-3 lg:grid-cols-[1fr_auto_auto_auto]">
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search Pal name…"
          aria-label="Search the tier list by Pal name"
          className="rounded-lg bg-background/50 border px-4 py-2 text-sm outline-none focus:ring-2 ring-primary"
        />
        <select
          value={role}
          onChange={(e) => set({ role: e.target.value })}
          aria-label="Rank Pals by role"
          className="rounded-lg bg-background/50 border px-3 py-2 text-sm"
        >
          {ROLES.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
        </select>
        <select
          value={element}
          onChange={(e) => set({ element: e.target.value })}
          aria-label="Filter tier list by element"
          className="rounded-lg bg-background/50 border px-3 py-2 text-sm"
        >
          <option value="">All elements</option>
          {ELEMENTS.map((el: Element) => <option key={el} value={el}>{el}</option>)}
        </select>
        <select
          value={rarity}
          onChange={(e) => set({ rarity: e.target.value })}
          aria-label="Filter tier list by rarity"
          className="rounded-lg bg-background/50 border px-3 py-2 text-sm"
        >
          <option value="">All rarities</option>
          {(["Common", "Uncommon", "Rare", "Legendary"] as Pal["rarity"][]).map((r) => (
            <option key={r} value={r}>{r}</option>
          ))}
        </select>
      </div>

      <div className="mt-4 flex flex-wrap gap-2" role="group" aria-label="Filter by tier">
        <button
          onClick={() => set({ tier: "" })}
          className={`rounded-full border px-3 py-1 text-xs font-semibold ${tier === "" ? "bg-primary text-primary-foreground" : "hover:bg-card"}`}
        >
          All tiers
        </button>
        {TIERS.map((t) => (
          <button
            key={t}
            onClick={() => set({ tier: tier === t ? "" : t })}
            aria-pressed={tier === t}
            className={`rounded-full border px-3 py-1 text-xs font-semibold ${tier === t ? "text-background" : "hover:bg-card"}`}
            style={tier === t ? { background: TIER_COLORS[t], borderColor: TIER_COLORS[t] } : { color: TIER_COLORS[t] }}
          >
            {t} tier · {counts[t]}
          </button>
        ))}
      </div>

      <p className="mt-4 text-sm text-muted-foreground">
        Showing {ranked.length} Pals ranked by {roleLabel.toLowerCase()}
        {tier ? ` in ${tier} tier` : ""}.
      </p>

      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 mt-4">
        {visible.map((p, i) => {
          const t = tierFor(p[field]);
          return (
            <Link key={p.id} to="/pals/$slug" params={{ slug: p.slug }} className="glass rounded-xl p-3 card-hover block">
              <PalPortrait pal={p} size={200} priority={i < 6} />
              <div className="mt-2 flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-sm font-semibold truncate">{p.name}</div>
                  <div className="text-[10px] text-muted-foreground">{roleLabel} {p[field].toFixed(1)}/10</div>
                </div>
                <span
                  className="text-[10px] font-bold px-1.5 py-0.5 rounded-full shrink-0"
                  style={{ background: TIER_COLORS[t] + "22", color: TIER_COLORS[t] }}
                >
                  {t}
                </span>
              </div>
              <div className="mt-1 text-[10px]" style={{ color: ELEMENT_COLORS[p.element] }}>
                {p.element} · {p.rarity}
              </div>
            </Link>
          );
        })}
        {ranked.length === 0 && (
          <div className="col-span-full text-center py-16 text-muted-foreground">No Pals match those filters.</div>
        )}
      </div>
      <div ref={sentinelRef} aria-hidden="true" className="h-1" />
      {hasMore && (
        <div className="mt-6 text-center">
          <button onClick={showMore} className="rounded-md border px-4 py-2 text-sm font-medium hover:bg-card">
            Show {Math.min(48, remaining)} more Pals
          </button>
        </div>
      )}

      <FaqSection items={FAQ} heading="Palworld tier list FAQ" />
      <RelatedGuides
        links={[
          { slug: "tier-list", label: "Meta Tier List — PvE & PvP Breakdown" },
          { slug: "best-mounts", label: "Best Flying & Ground Mounts" },
          { slug: "palworld-breeding-guide", label: "Palworld Breeding Guide" },
          { slug: "passive-skill-inheritance", label: "Passive Skill Inheritance Guide" },
        ]}
      />
    </div>
  );
}
